import React, { Suspense, lazy } from "react"
import { Spinner } from "reactstrap"
import Layout from "components/Layout"
import Authmiddleware from "./route"
import roleTypes from "constants/role"

// eslint-disable-next-line react/display-name
const Loadable = Component => props =>
  (
    <Suspense fallback={<Spinner color="primary">Loading...</Spinner>}>
      <Component {...props} />
    </Suspense>
  )

// ---------------------------- AUTHENTICATION PAGES ----------------------------
const Authentication = Loadable(
  lazy(() => import("../pages/Authentication/AutoLogin"))
)
const Login = Loadable(lazy(() => import("pages/Authentication/Login")))
const Logout = Loadable(lazy(() => import("pages/Authentication/Logout")))
const Register = Loadable(lazy(() => import("pages/Authentication/Register")))
const ForgetPwd = Loadable(
  lazy(() => import("pages/Authentication/ForgetPassword"))
)

// ---------------------------- ERROR PAGES ----------------------------
const ForbiddenError = Loadable(
  lazy(() => import("pages/Utility/ErrorUnauthorized"))
)
const NotFoundError = Loadable(
  lazy(() => import("pages/Utility/ErrorNotFound"))
)
const ServerError = Loadable(lazy(() => import("pages/Utility/ErrorServer")))

// ------------------------ ADMIN PORTAL PAGES ------------------------
const AdminUsers = Loadable(lazy(() => import("pages/Admin/Users/Admins")))
const AgencyUsers = Loadable(lazy(() => import("pages/Admin/Users/Agencies")))
const AdminDashboard = Loadable(lazy(() => import("pages/Admin/Dashboard")))
// const Emarketplace = Loadable(lazy(() => import("pages/Admin/EMarketPlace")))
const DefineProductSpecifications = Loadable(
  lazy(() => import("pages/Admin/Products/DefineProductSpecifications"))
)
const ItemSpecificationList = Loadable(
  lazy(() => import("pages/Admin/ItemMaster/ItemSpecificationList"))
)
const MerchantUsers = Loadable(
  lazy(() => import("pages/Admin/Users/Merchants"))
)
const SubCategories = Loadable(
  lazy(() => import("pages/Admin/Products/SubCategories"))
)
const SubSubCategory = Loadable(
  lazy(() => import("pages/Admin/Products/SubSubCategories"))
)
const ViewProduct = Loadable(
  lazy(() => import("pages/Admin/ProductListing/ViewProduct"))
)
const ViewMerchant = Loadable(
  lazy(() => import("pages/Admin/Users/Merchants/ViewMerchant"))
)
const ViewAdmin = Loadable(
  lazy(() => import("pages/Admin/Users/Admins/ViewAdmin"))
)
const ViewAgency = Loadable(
  lazy(() => import("pages/Admin/Users/Agencies/ViewAgency"))
)
const ParentCategories = Loadable(
  lazy(() => import("pages/Admin/Products/ParentCategories"))
)
const Category = Loadable(
  lazy(() => import("pages/Admin/Products/ParentCategories/Category"))
)
const SubCategory = Loadable(
  lazy(() => import("pages/Admin/Products/SubCategories/SubCategory"))
)
const ViewSubSubCategory = Loadable(
  lazy(() => import("pages/Admin/Products/SubSubCategories/SubSubCategory"))
)
const TableListing = Loadable(
  lazy(() => import("pages/Admin/ProductListing/TableListing"))
)
const PublishedListing = Loadable(
  lazy(() => import("pages/Admin/ProductListing/PublishedListing"))
)

// ------------------------ MERCHANT PORTAL PAGES ------------------------
const MerchantDashboard = Loadable(
  lazy(() => import("pages/Merchant/Dashboard/index"))
)
const MerchantProducts = Loadable(
  lazy(() => import("pages/Merchant/Products/MyProducts"))
)
const AddProduct = Loadable(
  lazy(() => import("pages/Merchant/Products/AddProduct"))
)
const MerchantEditProduct = Loadable(
  lazy(() => import("pages/Merchant/Products/EditProduct"))
)
const MerchantViewProduct = Loadable(
  lazy(() => import("pages/Merchant/Products/ViewProduct"))
)

const ActiveOrders = Loadable(
  lazy(() => import("pages/Merchant/Orders/ActiveOrders"))
)
const ViewActiveOrderDetails = Loadable(
  lazy(() => import("pages/Merchant/Orders/ViewActiveOrderDetails"))
)

const OrderHistory = Loadable(
  lazy(() => import("pages/Merchant/Orders/OrderHistory"))
)
const ViewOrderHistoryDetails = Loadable(
  lazy(() => import("pages/Merchant/Orders/ViewOrderHistoryDetails"))
)

// ------------------------ AGENCY PORTAL PAGES ------------------------
const AgencyHome = Loadable(lazy(() => import("pages/Agency/Home")))
const AgencyCategory = Loadable(lazy(() => import("pages/Agency/Category")))
const AgencyCompareItems = Loadable(
  lazy(() => import("pages/Agency/CompareItems"))
)
const AgencyMyCart = Loadable(lazy(() => import("pages/Agency/MyCart")))
const AgencyCheckOut = Loadable(
  lazy(() => import("pages/Agency/CheckOut/CheckOut"))
)
const AgencyMyOrders = Loadable(lazy(() => import("pages/Agency/MyOrders")))
const AgencyOrderStatus = Loadable(
  lazy(() => import("pages/Agency/OrderStatus/OrderStatus"))
)
const AgencyViewProduct = Loadable(
  lazy(() => import("pages/Agency/ViewProduct"))
)

const AgencyMyProfile = Loadable(
  lazy(() => import("pages/Agency/Profile/MyProfile"))
)
const AgencyEditProfile = Loadable(
  lazy(() => import("pages/Agency/Profile/EditProfile"))
)
const AgencyUpdatePassword = Loadable(
  lazy(() => import("pages/Agency/ChangePassword/UpdatePassword"))
)

const authProtectedRoutes = [
  // ADMIN ROUTES ------------------------------
  {
    path: "admin",
    element: (
      <Authmiddleware role={roleTypes["ADMIN"]}>
        <Layout />
      </Authmiddleware>
    ),
    children: [
      {
        path: "dashboard",
        element: <AdminDashboard />,
      },
      {
        path: "products/define-product-specifications/:id",
        element: <DefineProductSpecifications type="Edit" />,
      },
      {
        path: "products/define-product-specifications",
        element: <DefineProductSpecifications type="Add" />,
      },
      {
        path: "item-master/item-specification",
        element: <ItemSpecificationList />,
      },
      {
        path: "product-listing-pending",
        element: <TableListing type="Pending" />,
      },
      {
        path: "product-listing-published",
        element: <PublishedListing type="published" />,
      },
      {
        path: "product-listing/:id",
        element: <ViewProduct />,
      },

      {
        path: "parent-categories",
        children: [
          {
            index: true,
            element: <ParentCategories />,
          },
          {
            path: "add",
            element: <Category type="Add" />,
          },
          {
            path: ":id",
            element: <Category type="Edit" />,
          },
        ],
      },
      {
        path: "sub-categories",
        children: [
          {
            index: true,
            element: <SubCategories />,
          },
          {
            path: "add",
            element: <SubCategory type="Add" />,
          },
          {
            path: ":id",
            element: <SubCategory type="Edit" />,
          },
        ],
      },
      {
        path: "sub-sub-categories",
        children: [
          {
            index: true,
            element: <SubSubCategory />,
          },
          {
            path: "add",
            element: <ViewSubSubCategory type="Add" />,
          },
          {
            path: ":id",
            element: <ViewSubSubCategory type="Edit" />,
          },
        ],
      },
      // {
      //   // 2nd path
      //   path: "emarketplace",
      //   element: <Emarketplace />,
      // },
      // {
      //   // 2nd path
      //   path: "merchants",
      //   children: [
      //     {
      //       // Child Direct Page Component of /users/merchants
      //       index: true,
      //       element: <MerchantUsers />,
      //     },
      //     {
      //       path: ":id",
      //       element: <ViewMerchant type="Add" />,
      //     },
      //   ],
      // },
      // {
      //   // 2nd path
      //   path: "admins",
      //   children: [
      //     {
      //       // Child Direct Page Component of /users/admins
      //       index: true,
      //       element: <AdminUsers />,
      //     },
      //     {
      //       path: "add",
      //       element: <ViewAdmin page="Add" />,
      //     },
      //     {
      //       path: ":id",
      //       element: <ViewAdmin page="Edit" />,
      //     },
      //   ],
      // },
      // {
      //   // 2nd path
      //   path: "agencies",
      //   children: [
      //     {
      //       // Child Direct Page Component of /users/admins
      //       index: true,
      //       element: <AgencyUsers />,
      //     },
      //     {
      //       path: "add",
      //       element: <ViewAgency page="Add" />,
      //     },
      //     {
      //       path: ":id",
      //       element: <ViewAgency page="Edit" />,
      //     },
      //   ],
      // },
    ],
  },

  // MERCHANT ROUTES ------------------------------
  {
    // 1st path
    path: "merchant",
    element: (
      <Authmiddleware role={roleTypes["MERCHANT"]}>
        <Layout />
      </Authmiddleware>
    ),
    children: [
      {
        // 2nd path
        path: "dashboard",
        element: <MerchantDashboard />,
      },
      {
        // 2nd path
        path: "products",
        children: [
          {
            // Child Direct Page Component of /merchant/products
            index: true,
            element: <MerchantProducts />,
          },
          // Grandchild or 3rd path
          {
            path: "add",
            element: <AddProduct />,
          },
          {
            path: ":id/view",
            element: <MerchantViewProduct />,
          },
          {
            path: ":id/edit",
            element: <MerchantEditProduct />,
          },
        ],
      },
      {
        // 2nd path
        path: "active-orders-list",
        children: [
          {
            // Child Direct Page Component of /merchant/orders
            index: true,
            element: <ActiveOrders />,
          },
          // Grandchild or 3rd path
          {
            path: "view-active-order-details",
            element: <ViewActiveOrderDetails />,
          },
        ],
      },
      {
        // 2nd path
        path: "order-history-list",
        children: [
          {
            // Child Direct Page Component of /merchant/orders
            index: true,
            element: <OrderHistory />,
          },
          // Grandchild or 3rd path
          {
            path: "view-order-history-details",
            element: <ViewOrderHistoryDetails />,
          },
        ],
      },
    ],
  },

  // AGENCY ROUTES ------------------------------
  {
    // 1st path
    path: "agency",
    element: (
      <Authmiddleware role={roleTypes["AGENCY"]}>
        <Layout />
      </Authmiddleware>
    ),
    children: [
      {
        // 2nd path
        path: "home",
        element: <AgencyHome />,
      },
      {
        // 2nd path
        path: "category",
        element: <AgencyCategory />,
      },
      {
        // 2nd path
        path: "check-out",
        element: <AgencyCheckOut />,
      },
      {
        // 2nd path
        path: "compare-items",
        element: <AgencyCompareItems />,
      },
      {
        // 2nd path
        path: "order-status",
        element: <AgencyOrderStatus />,
      },
      {
        // 2nd path
        path: "view-product/:id",
        element: <AgencyViewProduct />,
      },
      {
        // 2nd path
        path: "my-cart",
        element: <AgencyMyCart />,
      },
      {
        // 2nd path
        path: "my-orders",
        element: <AgencyMyOrders />,
      },
      {
        // 2nd path
        path: "my-profile",
        element: <AgencyMyProfile />,
      },
      {
        // 2nd path
        path: "edit-profile",
        element: <AgencyEditProfile />,
      },
      {
        // 2nd path
        path: "change-password",
        element: <AgencyUpdatePassword />,
      },
    ],
  },

  // WILD CARD (DEFAULT MAIN ROUTES) localhost:3000 or main endpt----
  {
    path: "*",
    children: [
      {
        path: "", // equivalent to 👉 / to fix react-router bug
        element: <Login />,
      },
      {
        path: "403",
        element: <ForbiddenError />,
      },
      {
        path: "404",
        element: <NotFoundError />,
      },
      {
        path: "500",
        element: <ServerError />,
      },
      {
        path: "*",
        element: <NotFoundError />,
      },
    ],
  },

  // PUBLIC ROUTES ------------------------------------------
  { path: "authenticate", element: <Authentication /> },
  { path: "login", element: <Login /> },
  { path: "logout", element: <Logout /> },
  { path: "forgot-password", element: <ForgetPwd /> },
  { path: "register", element: <Register /> },
]

export { authProtectedRoutes }
